import { OverlayScrollbars } from 'overlayscrollbars';
import { useEffect, useRef, type RefObject } from 'react';

const TABLE_BODY_SELECTOR = '.ant-table-body';

const defaultOptions = {
  overflow: {
    x: 'scroll',
    y: 'scroll',
  },
  scrollbars: {
    // Windows: keep bars always visible. autoHide 'scroll'/'leave' often never paint
    // after rc-table remounts `.ant-table-body` when `scroll.x` flips.
    autoHide: 'never',
  },
} as const;

type Options = {
  enabled?: boolean;
  /** Fired on the scrollable viewport (infinite-scroll, etc.). */
  onScroll?: (viewport: HTMLElement) => void;
};

/**
 * Attach OverlayScrollbars to Ant Design Table body (`.ant-table-body`).
 * Avoids Windows classic scrollbar layout flicker; keeps header `scrollLeft` in sync.
 *
 * Follows the current body element for the whole mount: rc-table remounts its subtree when
 * `scroll.x` flips between unset and set (it wraps the table in a ResizeObserver), which
 * replaces `.ant-table-body`. A MutationObserver re-attaches to the new element; nothing is
 * re-initialised on plain data changes — OverlayScrollbars observes content size itself.
 * `onScroll` always listens on the live scroll element (OS viewport or native body) so
 * load-more keeps working even if OverlayScrollbars fails to init.
 */
export function useAntTableOverlayScrollbars(
  containerRef: RefObject<HTMLElement | null>,
  { enabled = true, onScroll }: Options = {},
): void {
  const onScrollRef = useRef(onScroll);
  onScrollRef.current = onScroll;

  useEffect(() => {
    if (!enabled) return;

    const root = containerRef.current;
    if (!root) return;

    let instance: OverlayScrollbars | null = null;
    let scrollTarget: HTMLElement | null = null;
    let attachedBody: HTMLElement | null = null;
    let cancelled = false;

    const syncHeaderScroll = (viewport: HTMLElement) => {
      const header = root.querySelector<HTMLElement>('.ant-table-header');
      if (header && header.scrollLeft !== viewport.scrollLeft) {
        header.scrollLeft = viewport.scrollLeft;
      }
    };

    const handleScroll = (event: Event) => {
      const viewport = event.currentTarget as HTMLElement;
      syncHeaderScroll(viewport);
      onScrollRef.current?.(viewport);
    };

    const detachScrollListener = () => {
      if (!scrollTarget) return;
      scrollTarget.removeEventListener('scroll', handleScroll);
      scrollTarget = null;
    };

    const destroy = () => {
      detachScrollListener();
      if (instance && OverlayScrollbars.valid(instance)) {
        instance.destroy();
      }
      instance = null;
      attachedBody = null;
    };

    const attach = (): boolean => {
      if (cancelled) return false;
      const body = root.querySelector<HTMLElement>(TABLE_BODY_SELECTOR);
      if (!body) return false;

      // Same body already wired — nothing to do.
      if (attachedBody === body && scrollTarget) {
        return true;
      }

      destroy();

      body.setAttribute('data-overlayscrollbars-initialize', '');
      try {
        instance = OverlayScrollbars(body, defaultOptions);
      } catch {
        // ponytail: OS init can fail on transient Ant Table DOM; native scroll still loads pages.
        instance = null;
      }

      if (instance && OverlayScrollbars.valid(instance)) {
        scrollTarget = instance.elements().viewport;
      } else {
        instance = null;
        scrollTarget = body;
      }

      attachedBody = body;
      scrollTarget.addEventListener('scroll', handleScroll, { passive: true });
      return true;
    };

    attach();

    // Keep observing: Ant Table swaps `.ant-table-body` when scroll props update.
    const observer = new MutationObserver(() => {
      if (cancelled) return;
      const body = root.querySelector<HTMLElement>(TABLE_BODY_SELECTOR);
      if (!body) return;
      if (attachedBody !== body || !scrollTarget) {
        attach();
      }
    });
    observer.observe(root, { childList: true, subtree: true });

    return () => {
      cancelled = true;
      observer.disconnect();
      destroy();
    };
  }, [containerRef, enabled]);
}
